<template>
  <div class="d-flex flex-column comparison-tooltip">
    <table>
      <thead>
        <tr>
          <th></th>
          <th>{{ metric }}</th>
          <th v-if="cumulative">{{ periodLabel }}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="text-right">
            <div>{{ baseDate }}</div>
            <div class="period-label">({{ offset }} {{ period }})</div>
          </td>
          <td>
            <div>{{ formatValue(base.value) }}</div>
            <div
              :class="{
                green: isGreen(baseValueDelta),
                red: isRed(baseValueDelta)
              }"
            >
              {{ formatPercent(baseValueDelta) }}
              <i :class="getDeltaIcon(baseValueDelta)"></i>
            </div>
          </td>
          <td v-if="cumulative">
            <div>{{ formatValue(base.cumulative) }}</div>
            <div
              :class="{
                green: isGreen(baseCumulativeDelta),
                red: isRed(baseCumulativeDelta)
              }"
            >
              {{ formatPercent(baseCumulativeDelta) }}
              <i :class="getDeltaIcon(baseCumulativeDelta)"></i>
            </div>
          </td>
        </tr>
        <tr v-if="comparison">
          <td class="text-right">
            <div>{{ comparisonDate }}</div>
            <div class="period-label">{{ comparisonLabel }}</div>
          </td>
          <td>
            <div>{{ formatValue(comparison.value) }}</div>
          </td>
          <td v-if="cumulative">
            <div>{{ formatValue(comparison.cumulative) }}</div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { formatDateLabel } from "@/lib/charts.js";
import { isFalsy } from "@/lib/helpers.js";
import { getQueryVariables } from "@/lib/url.js";

export default {
  props: {
    point: {
      type: Object,
      default: null
    },
    metric: {
      type: String,
      default: "Metric"
    },
    formatValue: {
      type: Function,
      default: () => {}
    },
    cumulative: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: ""
    },
    formatDate: {
      type: Boolean,
      default: false
    },
    invertColor: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      dashboardFilters: "dashboardFilters",
      periodLabel: ({ dashboardFilters }) => {
        switch (dashboardFilters.period) {
          case "30": {
            return "30 Days";
          }
          case "week": {
            return "WTD";
          }
          case "month": {
            return "MTD";
          }
          case "quarter": {
            return "QTD";
          }
          case "year": {
            return "YTD";
          }
          default:
            return dashboardFilters.period;
        }
      },
      period: ({ dashboardFilters }) => {
        return dashboardFilters.period;
      },
      offset: ({ dashboardFilters }) => {
        return dashboardFilters.offset;
      }
    }),
    base() {
      return this.point ? this.point.base : null;
    },
    comparison() {
      return this.point ? this.point.comparison : null;
    },
    baseCumulativeDelta() {
      const comparisonValue = this.comparison ? this.comparison.cumulative : 0;
      return this.getDelta(this.base.cumulative, comparisonValue);
    },
    baseValueDelta() {
      const comparisonValue = this.comparison ? this.comparison.value : 0;
      return this.getDelta(this.base.value, comparisonValue);
    },
    baseDate() {
      return this.formatDate
        ? formatDateLabel(
            this.base,
            this.dashboardFilters.period,
            this.cumulative
          )
        : this.base.date;
    },
    comparisonDate() {
      return this.formatDate
        ? formatDateLabel(
            this.comparison,
            this.dashboardFilters.period,
            this.cumulative
          )
        : this.comparison.date;
    },
    comparisonLabel() {
      const { comparisonPeriod } = getQueryVariables();
      return comparisonPeriod === "samePeriod"
        ? `(vs Prior ${this.period})`
        : `(vs Same ${this.period} Prior Year)`;
    }
  },
  methods: {
    formatTooltipValue(point) {
      const daily = isFalsy(point.value) ? "--" : point.value;
      const cumulative = isFalsy(point.cumulative) ? "--" : point.cumulative;
      return `${cumulative} (${daily})`;
    },
    getDelta(base, comparison) {
      if (comparison > 0) {
        const difference = (base || 0) - comparison;
        return this.type === "percentage"
          ? difference
          : difference / comparison;
      }
      return 0;
    },
    getDeltaIcon(delta) {
      if (delta === 0) {
        return "";
      }
      return delta > 0 ? "fa fa-arrow-up" : "fa fa-arrow-down";
    },
    isGreen(delta) {
      return this.invertColor ? delta < 0 : delta > 0;
    },
    isRed(delta) {
      return this.invertColor ? delta > 0 : delta < 0;
    }
  }
};
</script>

<style lang="scss" scoped>
.comparison-tooltip {
  hr {
    margin: 2px -5px;
    background: #fff;
    opacity: 0.8;
  }
  table {
    th,
    td {
      text-align: center;
      padding: 4px 4px;
    }
    tbody tr {
      border-top: 1px solid rgba(255, 255, 255, 0.3);
    }
  }
}
.green {
  color: var(--organic-green) !important;
}
.red {
  color: var(--danger-color) !important;
}
.period-label {
  font-style: italic;
  color: rgba(255, 255, 255, 0.5);
}
</style>
